import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Button, Alert, Empty, Modal, Input, Tooltip, Space } from 'antd';
import { ReloadOutlined, LinkOutlined, WarningOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import mobileService from '../../services/mobileService';
import { formatDate, formatDateHeure, can, HAB } from '../../variables/constants';

import i18n from '../../i18n';
import { avecLibelleTraduit } from '../../i18n/libelles';

const { TextArea } = Input;
const MUTED = '#6b7280';

/**
 * Rattachements usager ↔ dossier en attente de confirmation.
 *
 * <h3>Ce que l'agent tranche ici</h3>
 * Confirmer, c'est ouvrir a une personne l'acces a un dossier d'indemnisation :
 * ses montants, ses pieces, son offre. Refuser, c'est le lui fermer. Le backend
 * qualifie l'acte de « nominatif et non reversible » - l'ecran demande donc une
 * confirmation explicite, jamais un simple clic de liste.
 *
 * <h3>L'origine est le coeur de la decision</h3>
 * `DECLARATION` : l'usager a lui-meme cree le dossier depuis l'application. Le
 * lien decoule d'un acte, il est fort.
 * `RAPPROCHEMENT_TEL` : le systeme a rapproche sur un NUMERO DE TELEPHONE. Signal
 * faible et usurpable - un numero se recycle, se prete, se declare. Les deux ne
 * se confirment pas avec la meme legerete ; l'ecran le dit en clair plutot que
 * d'afficher un code d'enumeration que personne n'interprete.
 */

const ORIGINE = {
  DECLARATION: {
    color: 'green',
  },
  RAPPROCHEMENT_TEL: {
    color: 'orange',
  },
};

avecLibelleTraduit(ORIGINE, 'mobile:rattachements.originesLibelles');
avecLibelleTraduit(ORIGINE, 'mobile:rattachements.originesAides', { champ: 'aide' });

export default function RattachementsAConfirmer() {
  const { t } = useTranslation('mobile');
  const navigate = useNavigate();
  const [lignes, setLignes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [refus, setRefus] = useState({ open: false, ligne: null });
  const [motif, setMotif] = useState('');
  const [enCours, setEnCours] = useState(false);

  const { current, pageSize } = pagination;
  const peutLire = can(HAB.MOBILE_RATTACHEMENT_READ);
  const peutTrancher = can(HAB.MOBILE_RATTACHEMENT_CONFIRMER);

  const charger = useCallback(async (page, taille) => {
    setChargement(true);
    setErreur(null);
    try {
      const res = await mobileService.rattachements({ page, size: taille });
      setLignes(res.items);
      setPagination((p) => ({ ...p, current: res.page, pageSize: res.pageSize, total: res.total }));
    } catch (err) {
      setErreur(err?.response?.data?.message || i18n.t('mobile:rattachements.erreurChargement'));
      setLignes([]);
      setPagination((p) => ({ ...p, total: 0 }));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    if (peutLire) charger(current, pageSize);
    else setChargement(false);
  }, [charger, current, pageSize, peutLire]);

  // Une demande tranchee quitte la file : on la retire tout de suite plutot que
  // d'attendre le rechargement, sinon elle reste cliquable quelques centaines de ms.
  const apresDecision = (id) => {
    setLignes((prev) => prev.filter((l) => l.rattachementTrackingId !== id));
    setPagination((p) => ({ ...p, total: Math.max(p.total - 1, 0) }));
  };

  const confirmer = (r) => {
    Modal.confirm({
      title: t('rattachements.confirmation.titre'),
      icon: <WarningOutlined style={{ color: '#d97706' }} />,
      width: 560,
      content: (
        <div style={{ fontSize: 13 }}>
          <p>
            <Trans
              t={t}
              i18nKey="rattachements.confirmation.acces"
              values={{
                nom: [r.usagerPrenom, r.usagerNom].filter(Boolean).join(' ') || t('rattachements.confirmation.cetUsager'),
                numero: r.numeroSinistreLocal || '-',
              }}
              components={{ strong: <strong /> }}
            />
          </p>
          {r.origine === 'RAPPROCHEMENT_TEL' && (
            <p style={{ color: '#b45309', marginBottom: 6 }}>
              <Trans t={t} i18nKey="rattachements.confirmation.signalFaible" components={{ strong: <strong /> }} />
            </p>
          )}
          {!r.usagerIdentiteVerifiee && (
            <p style={{ color: '#b45309', marginBottom: 6 }}>
              {t('rattachements.confirmation.identiteNonControlee')}
            </p>
          )}
          <p style={{ color: MUTED, fontSize: 12, marginBottom: 0 }}>
            {t('rattachements.confirmation.irreversible')}
          </p>
        </div>
      ),
      okText: t('rattachements.confirmation.ok'),
      cancelText: t('rattachements.confirmation.annuler'),
      okButtonProps: { style: { background: '#1a6b3c', borderColor: '#1a6b3c' } },
      onOk: async () => {
        try {
          await mobileService.deciderRattachement(r.rattachementTrackingId, { confirme: true });
          toast.success(t('rattachements.confirme'));
          apresDecision(r.rattachementTrackingId);
        } catch (err) {
          toast.error(err?.response?.data?.message || t('rattachements.confirmationImpossible'));
        }
      },
    });
  };

  const refuser = async () => {
    // Le backend exige un motif au refus : un refus muet est inexploitable, et
    // l'usager se verrait fermer l'acces sans que personne puisse dire pourquoi.
    if (!motif.trim()) {
      toast.warning(t('rattachements.motifObligatoire'));
      return;
    }
    setEnCours(true);
    try {
      await mobileService.deciderRattachement(refus.ligne.rattachementTrackingId, {
        confirme: false, motifRefus: motif.trim(),
      });
      toast.success(t('rattachements.refuse'));
      apresDecision(refus.ligne.rattachementTrackingId);
      setRefus({ open: false, ligne: null });
      setMotif('');
    } catch (err) {
      toast.error(err?.response?.data?.message || t('rattachements.refusImpossible'));
    } finally {
      setEnCours(false);
    }
  };

  const colonnes = [
    {
      title: t('rattachements.colonnes.usager'),
      key: 'usager',
      width: 240,
      render: (_, r) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 650, display: 'flex', alignItems: 'center', gap: 6 }}>
            {[r.usagerPrenom, r.usagerNom].filter(Boolean).join(' ') || '-'}
            {!r.usagerIdentiteVerifiee && (
              <Tooltip title={t('rattachements.identiteNonControleeAgent')}>
                <WarningOutlined style={{ color: '#d97706' }} />
              </Tooltip>
            )}
          </div>
          <div style={{ fontSize: 12, color: MUTED }}>{r.usagerTelephone || '-'}</div>
          <div style={{
            fontSize: 12, color: MUTED,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {r.usagerEmail || '-'}
          </div>
        </div>
      ),
    },
    {
      title: t('rattachements.colonnes.dossier'),
      key: 'dossier',
      width: 210,
      render: (_, r) => (
        <div style={{ minWidth: 0 }}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, fontWeight: 700, height: 'auto' }}
            onClick={() => navigate(`/sinistres/${r.sinistreTrackingId}`)}
          >
            {r.numeroSinistreLocal || '-'}
          </Button>
          <div style={{ fontSize: 12, color: MUTED }}>
            {r.dateDeclarationSinistre ? t('rattachements.declareLe', { date: formatDate(r.dateDeclarationSinistre) }) : '-'}
          </div>
        </div>
      ),
    },
    {
      title: t('rattachements.colonnes.origine'),
      dataIndex: 'origine',
      width: 220,
      render: (v) => {
        const o = ORIGINE[v];
        if (!o) return <span style={{ color: '#9ca3af' }}>{v || '-'}</span>;
        return (
          <Tooltip title={o.aide}>
            <Tag color={o.color} style={{ borderRadius: 12, marginInlineEnd: 0, fontWeight: 600 }}>
              {o.label}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: t('rattachements.colonnes.proposeLe'),
      dataIndex: 'dateProposition',
      width: 160,
      render: (v) => <span style={{ fontSize: 12.5 }}>{v ? formatDateHeure(v) : '-'}</span>,
    },
    {
      title: '',
      key: 'actions',
      width: 190,
      align: 'right',
      render: (_, r) => (
        <Space size={6}>
          <Button size="small" danger disabled={!peutTrancher}
            onClick={() => { setRefus({ open: true, ligne: r }); setMotif(''); }}>
            {t('rattachements.refuser')}
          </Button>
          <Button size="small" type="primary" disabled={!peutTrancher}
            style={peutTrancher ? { background: '#1a6b3c', borderColor: '#1a6b3c' } : undefined}
            onClick={() => confirmer(r)}>
            {t('rattachements.confirmer')}
          </Button>
        </Space>
      ),
    },
  ];

  if (!peutLire) {
    return (
      <Layout title={t('rattachements.titre')}>
        <Alert type="warning" showIcon
          message={t('rattachements.reserveLecture')} />
      </Layout>
    );
  }

  return (
    <Layout title={t('rattachements.titre')}>
      <PageHeader
        titre={t('rattachements.titre')}
        icone={<LinkOutlined />}
        compte={chargement ? undefined : pagination.total}
        unite={t('rattachements.unite')}
        actions={
          <Button icon={<ReloadOutlined />} onClick={() => charger(current, pageSize)}>
            {t('rattachements.actualiser')}
          </Button>
        }
      />

      {!peutTrancher && (
        <Alert
          type="info"
          showIcon
          message={t('rattachements.consultationSeule')}
          style={{ marginBottom: 16 }}
        />
      )}

      {erreur && (
        <Alert type="error" showIcon message={erreur} style={{ marginBottom: 16 }}
          action={<Button size="small" onClick={() => charger(current, pageSize)}>{t('rattachements.reessayer')}</Button>} />
      )}

      <Table
        className="cb-table"
        rowKey="rattachementTrackingId"
        dataSource={lignes}
        columns={colonnes}
        loading={chargement}
        size="middle"
        scroll={{ x: 1020 }}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('rattachements.aucun')} />
          ),
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          showTotal: (n) => t('rattachements.total', { count: n }),
          onChange: (page, taille) => setPagination((p) => ({ ...p, current: page, pageSize: taille })),
        }}
      />

      <Modal
        open={refus.open}
        title={t('rattachements.modaleRefus.titre')}
        okText={t('rattachements.modaleRefus.refuser')}
        cancelText={t('rattachements.modaleRefus.annuler')}
        okButtonProps={{ danger: true, loading: enCours, disabled: !motif.trim() }}
        onOk={refuser}
        onCancel={() => { setRefus({ open: false, ligne: null }); setMotif(''); }}
        destroyOnClose
      >
        <p style={{ fontSize: 13 }}>
          <Trans
            t={t}
            i18nKey="rattachements.modaleRefus.texte"
            values={{ numero: refus.ligne?.numeroSinistreLocal || '-' }}
            components={{ strong: <strong /> }}
          />
        </p>
        <TextArea
          rows={4}
          maxLength={500}
          showCount
          placeholder={t('rattachements.modaleRefus.motif')}
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
        />
      </Modal>
    </Layout>
  );
}
